import { Component, Element, Event, EventEmitter, h, Prop, Watch, State } from "@stencil/core";
import Fragment from "../../../../common/fragment";
import {
  VideoplayerProgressEventArgs,
  VideoplayerLocalization,
  VideoPlayerType,
  ImageVideoMeta
} from "../../constants/videoplayer-types";
import { Player } from "../player";
import { isSafari } from "../../../../utils/device-helper";
import pfx from "../../../../utils/style-class-prefix-helper";
import { VIDEOPLAYER_TYPE_FILE, VIDEOPLAYER_TYPE_MOVING_IMAGE } from "../../constants/videoplayer-constants";
import webComponentsDebug from "../../../../utils/log-helper";
import { StyledComponent } from "../../../../decorators/styled-component";
// import { getLocalizationLocale } from "../../../../utils/localization-helper";
// import { InitLogging } from "../../../../decorators/init-logging";

@Component({
  tag: "eon-ui-videoplayer-file",
  styleUrls: ["../player-shared.scss", "file-player.scss"],
  shadow: false
})
export class FilePlayer implements Player {
  // @InitLogging() sentryTransaction;

  @StyledComponent() component: any;
  @Element() hostElement: HTMLEonUiVideoplayerFileElement;
  private videoElement: HTMLVideoElement;

  @State() videoSourcesJson: ImageVideoMeta;
  @State() resolvedSource: any;
  @State() resolvedTrack: string | undefined;

  /**
   * Raised when the browser finished analyzing the video and can start playing, meaning it has buffered enough to begin.
   * Emits the total length of the video in seconds.
   */
  @Event() canPlayEvent: EventEmitter<number>;

  /**
   * Raised when the video has reached the end.
   */
  @Event() endedEvent: EventEmitter<void>;

  /**
   * Raised when the video resource could not be loaded due to an error (e.g. a network error).
   */
  @Event() errorEvent: EventEmitter<void>;

  /**
   * Raised when the time indicated by the currentTime attribute of the video has been updated.
   * This is triggered very frequently to keep the progress bar up2date.
   */
  @Event() timeUpdateEvent: EventEmitter<VideoplayerProgressEventArgs>;

  /**
   * Raised when a native video player (e.g. in Safari) toggles the fullscreen mode without using the custom E.ON videoplayer controls.
   */
  @Event() nativePlayerFullscreenChangeEvent: EventEmitter<void>;

  /**
   * The pauseEvent is fired when the video is paused via youtube button or customised control bar
   */
  @Event() pauseEvent: EventEmitter<void>;

  /**
   * The playEvent is fired when the video is played via youtube button or customised control bar
   */
  @Event() playEvent: EventEmitter<void>;

  /**
   * Flexibility to editor for choosing player: file player or moving image player?
   */
  @Prop() type: VideoPlayerType;

  /**
   * Set to true if the player should attempt to autoplay the video.
   * This doesn't work on iPhones and requires the video to be muted as well.
   */
  @Prop() autoplay: boolean;

  /**
   * Controls whether the video is muted or not.
   */
  @Prop() isMuted: boolean;

  /**
   * Controls whether the video is currently playing or not.
   * Set to false to pause the video.
   */
  @Prop() isPlaying: boolean;

  /**
   * The seconds at which the video should play.
   */
  @Prop() seekTime: number;

  /**
   * The source URL of the video.
   */
  @Prop() source: string;

  /**
   * optional vtt URL of the video
   */
  @Prop() track: string;

  /**
   * Determines whether the video is currently displayed in fullscreen mode or not.
   */
  @Prop() isFullscreen: boolean;

  /**
   * Show captions from track source
   */
  @Prop() captions: boolean;

  /**
   * Translated strings in the language currently selected.
   */
  @Prop() currentLocalization: VideoplayerLocalization;

  /**
   * Controls default control bar of the videoplayer.
   */
  @Prop() controls: boolean = false;

  /**
   * languageCode from videoplayer prop. Used for Image Video tracks only
   */
  @Prop() languageCode: string;

  componentWillLoad() {
    if (this.type === VIDEOPLAYER_TYPE_MOVING_IMAGE) {
      this.createPlayer();
    } else if (this.type === VIDEOPLAYER_TYPE_FILE) {
      this.resolvedSource = this.source;
      this.resolvedTrack = this.track;
    }
  }

  componentDidLoad() {
    if (isSafari()) {
      this.videoElement.addEventListener("webkitbeginfullscreen", this.handleNativePlayerFullscreenChange); //iPad
      this.videoElement.addEventListener("webkitfullscreenchange", this.handleNativePlayerFullscreenChange); // Desktop
    }
  }

  disconnectedCallback() {
    if (isSafari()) {
      this.videoElement.removeEventListener("webkitbeginfullscreen", this.handleNativePlayerFullscreenChange);
      this.videoElement.removeEventListener("webkitfullscreenchange", this.handleNativePlayerFullscreenChange);
    }
  }

  /**
   * Reference for MovingImage REST API:
   * @Here: https://doc.movingimage.com/display/LT/movingimage+Playout+API#/reference/0/get-playout-video/get-playout-video
   * This Returns API response which is then fetched in the form of JSON and accordingly video source is retrieved.
   */
  @Watch("type")
  @Watch("source")
  async createPlayer() {
    await fetch(`https://d.video-cdn.net/play/public/v1/video/${this.source}`, {
      method: "GET"
    }).then((response) =>
      response
        .json()
        .then((json) => {
          this.videoSourcesJson = json;
        })
        .catch((error) => {
          if (webComponentsDebug) {
            console.log(`MovingImage URL for video ID: ${this.source} failed to resolve: ${error}`);
          }
          // Here is errorEvent is fired for invalid MovingImage VideoIds
          this.errorEvent.emit();
        })
    );
    const htmlVideoSource = this.videoSourcesJson?.videoSources?.html["720p"] || null;
    this.resolvedSource = (htmlVideoSource?.length && htmlVideoSource[0]?.source) || null;

    /* experimental feature: use vtt from manifest
    if (!this.track) {
      const resolvedLanguage = this.languageCode ?? getLocalizationLocale(this.hostElement);
      const track = this.videoSourcesJson?.videoTracks.find((x) => x.language == resolvedLanguage);
      if (track) {
        try {
          const trackVttResponse = await fetch(track.source, { method: "GET" });
          const trackVttText = await trackVttResponse.text();
          this.resolvedTrack = URL.createObjectURL(new Blob([trackVttText], { type: "text/vtt" }));
        } catch (_) {
          this.resolvedTrack = undefined;
        }
      }
    } else {
      this.resolvedTrack = this.track;
    }
    */

    return this.resolvedSource;
  }

  handleNativePlayerFullscreenChange = (_e: Event) => {
    this.nativePlayerFullscreenChangeEvent.emit();
  };

  @Watch("isMuted")
  handleIsMutedChanged() {
    this.videoElement.muted = this.isMuted;
  }

  @Watch("seekTime")
  handleSeekTimeChanged() {
    this.videoElement.currentTime = this.seekTime;
  }

  @Watch("captions")
  handleShowCaptions() {
    const trackList = this.videoElement.textTracks ?? (([] as unknown) as TextTrackList);
    for (let i = 0; i < trackList.length; i++) {
      trackList[i].mode = this.captions ? "showing" : "hidden";
    }
  }

  private handleCanPlay(_e: Event): void {
    this.canPlayEvent.emit(this.videoElement.duration);
    this.handleShowCaptions();
  }

  private handleLoadedMetaData(_e: Event): void {
    // Safari doesn't fire handleCanPlay, so we have to use handleLoadedMetaData instead.
    if (isSafari()) {
      this.canPlayEvent.emit(this.videoElement.duration);
      this.handleShowCaptions();
    }
  }

  private handleEnded(_e: Event): void {
    // Reloads the video so that the poster-image is shown again after play.
    // This isn't browser default behaviour and therefore needs this workaround.
    // It shouldn't be a performance problem though because the video is always cached.
    this.videoElement.load();
    this.endedEvent.emit();
  }

  private handleError(_e: Event): void {
    if (this.type === VIDEOPLAYER_TYPE_FILE) {
      this.errorEvent.emit();
    }
  }

  private handleTimeUpdate(_e: Event): void {
    const percentPlayed = Math.round((100 / this.videoElement.duration) * this.videoElement.currentTime);
    const secondsPlayed = (percentPlayed / 100) * this.videoElement.duration;

    this.timeUpdateEvent.emit({ percent: percentPlayed, seconds: secondsPlayed });
  }

  private handleOnPlay(_e: Event): void {
    this.videoElement.play();
    this.playEvent.emit();
  }

  private handleOnPause(_e: Event): void {
    this.videoElement.pause();
    this.pauseEvent.emit();
  }

  @Watch("isPlaying")
  handleIsPlayingChanged() {
    if (this.isPlaying) {
      this.videoElement.play();
      this.playEvent.emit();
    } else {
      this.videoElement.pause();
      this.pauseEvent.emit();
    }
  }

  @Watch("isFullscreen")
  handleFullscreenChanged() {
    const trackList = this.videoElement.textTracks ?? (([] as unknown) as TextTrackList);
    for (let i = 0; i < trackList.length; i++) {
      for (let j = 0; j < trackList[i].cues.length; j++) {
        if (trackList[i].cues[j] instanceof VTTCue) {
          (trackList[i].cues[j] as any).line = this.isFullscreen ? -4 : "auto";
        }
      }
    }
  }

  render() {
    const sources = (
      <Fragment>
        <source src={this.resolvedSource} type="video/mp4" onError={(e) => this.handleError(e)} />
        {this.currentLocalization?.videoTagNotSupported}
        {this.resolvedTrack && <track default kind="captions" srclang={"de"} src={this.resolvedTrack} />}
      </Fragment>
    );
    const defaultProps = {
      controls: this.controls,
      ref: (el) => (this.videoElement = el),
      onCanPlay: (e) => this.handleCanPlay(e),
      onPlay: (e) => this.handleOnPlay(e),
      onPause: (e) => this.handleOnPause(e),
      onLoadedMetaData: (e) => this.handleLoadedMetaData(e),
      onEnded: (e) => this.handleEnded(e),
      onError: (e) => this.handleError(e),
      onTimeUpdate: (e) => this.handleTimeUpdate(e),
      class: pfx({ "fullscreen-safari": this.isFullscreen && isSafari() })
    };

    const videoPlayerProps =
      this.type === VIDEOPLAYER_TYPE_FILE
        ? defaultProps
        : {
            ...defaultProps,
            src: this.resolvedSource
          };

    return this.autoplay ? (
      // Do NOT change the sorting order of props for the autoplay video!
      // This will break the feature in some browsers because they expect the video to be muted before setting the autoplay flag.
      <video
        // @ts-ignore
        defaultMuted
        loop
        muted
        playsInline
        autoPlay
        preload="metadata"
        {...videoPlayerProps}
      >
        {sources}
      </video>
    ) : (
      // eslint-disable-next-line jsx-a11y/media-has-caption
      <video preload={isSafari() ? "auto" : "metadata"} {...videoPlayerProps}>
        {sources}
      </video>
    );
  }
}
